import { ReferenceLine } from "recharts"
import { shortDate } from "../dates"
import type { BasePoint, Grain, NumberedAction } from "../salesSeries"

// Shared pieces for the graphs in the product panel.

export const AXIS_TICK = { fontSize: 11, fill: "var(--chart-axis)" }

// How many points apart the date labels sit: every two weeks, every four
// weeks, every month.
const TICK_EVERY: Record<Grain, number> = { day: 14, week: 4, month: 1 }

export function xAxisProps(points: BasePoint[], grain: Grain) {
  return {
    dataKey: "i",
    ticks: points.filter((p) => p.i % TICK_EVERY[grain] === 0).map((p) => p.i),
    tickFormatter: (i: number) => points[i]?.tick ?? "",
    tick: AXIS_TICK,
    stroke: "var(--chart-grid)",
    interval: 0 as const, // show every tick listed, so "Oct (so far)" never drops out
  }
}

// The number(s) shown in an action circle: "1", "1, 2", or "1–3" when three
// or more actions fall on the same day, week or month.
export function badgeText(actions: NumberedAction[]) {
  const ns = actions.map((a) => a.n)
  return ns.length > 2 ? `${ns[0]}–${ns[ns.length - 1]}` : ns.join(", ")
}

// The same circle as on the graph, for the list under it and the tooltip.
export function ActionBadge({ text }: { text: string }) {
  return (
    <span className="inline-flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full bg-foreground px-1 text-[11px] font-semibold text-background tabular-nums">
      {text}
    </span>
  )
}

// A numbered circle sitting on top of the dashed line. The list under the
// graph says what each number is, so long action names never collide.
function BadgeLabel({ viewBox, text }: { viewBox?: { x: number; y: number }; text: string }) {
  if (!viewBox) return null
  const width = Math.max(18, text.length * 6.5 + 8)
  return (
    <g transform={`translate(${viewBox.x}, ${viewBox.y - 11})`}>
      <rect x={-width / 2} y={-9} width={width} height={18} rx={9} fill="var(--foreground)" />
      <text textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600} fill="var(--background)">{text}</text>
    </g>
  )
}

// One dashed line on each day, week or month a past action ran, with its
// number on top. Called as a function, not a component: recharts only reads
// its direct children.
export function actionMarkers(points: BasePoint[]) {
  return points.filter((p) => p.actions.length > 0).map((p) => (
    <ReferenceLine
      key={`action-${p.i}`}
      x={p.i}
      stroke="var(--foreground)"
      strokeDasharray="4 3"
      label={(props: { viewBox?: { x: number; y: number } }) => <BadgeLabel viewBox={props.viewBox} text={badgeText(p.actions)} />}
    />
  ))
}

export function PointTooltip<P extends BasePoint>({ active, payload, lines }: {
  active?: boolean
  payload?: { payload: P }[]
  lines: (point: P) => string[]
}) {
  if (!active || !payload?.length) return null
  const point = payload[0].payload
  return (
    <div className="rounded-md border bg-popover px-3 py-2 text-sm shadow-md">
      <div className="font-semibold">{point.title}</div>
      {lines(point).map((line) => <div key={line} className="tabular-nums">{line}</div>)}
      {point.actions.map((a) => (
        <div key={a.n} className="mt-1 flex items-center gap-1.5 font-medium">
          <ActionBadge text={String(a.n)} />
          {a.type}
        </div>
      ))}
    </div>
  )
}

// What each numbered circle on a graph stands for, listed under the graph.
export function ActionKey({ actions }: { actions: NumberedAction[] }) {
  if (actions.length === 0) return null
  return (
    <div className="mt-2 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-sm">
      {actions.map((a) => (
        <span key={a.n} className="flex items-center gap-1.5">
          <ActionBadge text={String(a.n)} />
          <span className="font-medium">{a.type}</span>
          <span>· {shortDate(a.date)}</span>
        </span>
      ))}
    </div>
  )
}
